import { CardShell } from '@atlas/ui-components'
import { Button, Chip } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { PageHeader } from '@/components/editorial/PageHeader'
import { PromptListDialog } from '@/components/prompts/PromptListDialog'
import type { PromptListRecord, PromptVariableListItem } from '@/domain/prompt.types'
import {
	useCreatePromptList,
	useDeletePromptList,
	usePromptListReferences,
	usePromptLists,
	useUpdatePromptList,
} from '@/hooks/usePrompts'
import { notification } from '@/notifications/notification-store'

export function PromptListsPage() {
	const lists = usePromptLists()
	const remove = useDeletePromptList()
	const create = useCreatePromptList()
	const update = useUpdatePromptList()
	/** Which list the user is about to delete — references are shown before it happens. */
	const [pending, setPending] = useState<PromptListRecord | null>(null)
	const [creating, setCreating] = useState(false)
	const [editing, setEditing] = useState<PromptListRecord | null>(null)

	return (
		<div>
			<PageHeader
				eyebrow="Library"
				title="Prompt lists"
				subtitle="Shared option sets that list-typed variables reference. Edited once, picked up by every prompt."
				actions={<Button onPress={() => setCreating(true)}>New list</Button>}
			/>

			{remove.isError ? (
				<p role="alert" className="mb-4 text-ds-sm text-ds-danger">
					That list could not be deleted — it is still referenced by a live prompt.
				</p>
			) : null}

			{lists.isLoading ? (
				<p className="text-ds-text-muted">Loading…</p>
			) : (lists.data ?? []).length === 0 ? (
				<p className="rounded-ds-card border border-dashed border-ds-border p-8 text-center text-ds-text-muted">
					No shared lists yet. Create one when two prompts need the same options.
				</p>
			) : (
				<ul className="space-y-3">
					{(lists.data ?? []).map((list) => (
						<CardShell as="li" key={list.id} className="p-4">
							<div className="flex items-start justify-between gap-3">
								<div>
									<p className="text-ds-sm font-medium text-ds-text">{list.name}</p>
									<p className="mt-1 font-mono text-ds-2xs text-ds-text-subtle">{list.code}</p>
								</div>
								<div className="flex items-center gap-2">
									<Button size="sm" variant="secondary" onPress={() => setEditing(list)}>
										Edit
									</Button>
									<Button size="sm" variant="muted" onPress={() => setPending(list)}>
										Delete
									</Button>
								</div>
							</div>

							<ListItems items={list.items} />
						</CardShell>
					))}
				</ul>
			)}

			<PromptListDialog
				open={creating || editing !== null}
				list={editing}
				existing={lists.data ?? []}
				submitting={create.isPending || update.isPending}
				errorMessage={
					create.isError || update.isError ? 'The list could not be saved. Your entries are still here.' : null
				}
				onCancel={() => {
					setCreating(false)
					setEditing(null)
					create.reset()
					update.reset()
				}}
				onSave={(input) => {
					const done = (verb: string) => () => {
						setCreating(false)
						setEditing(null)
						notification.success(`List “${input.name}” ${verb}.`)
					}

					if (editing) {
						// `code` is fixed on an edit, so only the mutable fields are sent —
						// the backend refuses a code change anyway.
						update.mutate(
							{ id: editing.id, patch: { name: input.name, items: input.items } },
							{ onSuccess: done('saved') },
						)
						return
					}
					create.mutate(input, { onSuccess: done('created') })
				}}
			/>

			{pending ? (
				<DeleteListConfirm
					list={pending}
					isDeleting={remove.isPending}
					onCancel={() => setPending(null)}
					onConfirm={() => {
						remove.mutate(pending.id, {
							onSuccess: () => {
								setPending(null)
								notification.success(`List “${pending.name}” deleted.`)
							},
						})
					}}
				/>
			) : null}
		</div>
	)
}

/**
 * A list's options, showing what each one SENDS when it differs from its label.
 *
 * A bare chip row showed labels only, which hid the thing most worth seeing: an
 * option can render as something else entirely, and "Animal Alone" sending "the
 * animal alone, no people in frame" is invisible unless the page says so. Moving
 * the phrasing out of code and into a column nobody can read would be worse than
 * leaving it in code.
 */
function ListItems({ items }: { items: PromptVariableListItem[] }) {
	const phrased = items.filter((item) => item.renderAs !== undefined && item.renderAs !== item.value)

	// No item overrides its render text, so a chip row says everything there is.
	if (phrased.length === 0) {
		return (
			<ul className="mt-3 flex flex-wrap gap-1.5">
				{items.map((item) => (
					<li key={item.value}>
						<Chip variant="muted" size="sm">
							{item.label}
						</Chip>
					</li>
				))}
			</ul>
		)
	}

	return (
		<details className="mt-3">
			<summary className="cursor-pointer text-ds-2xs uppercase tracking-wide text-ds-text-subtle">
				{items.length} option{items.length === 1 ? '' : 's'} · {phrased.length} with custom prompt text
			</summary>
			<ul className="mt-2 space-y-1">
				{items.map((item) => (
					<li key={item.value} className="flex flex-wrap items-baseline gap-2 text-ds-2xs">
						<span className="text-ds-text">{item.label}</span>
						<span className="font-mono text-ds-text-subtle">
							{item.renderAs !== undefined && item.renderAs !== item.value ? `→ ${item.renderAs}` : `→ ${item.value}`}
						</span>
					</li>
				))}
			</ul>
		</details>
	)
}

/**
 * Shows what references a list BEFORE the delete is attempted.
 *
 * The backend refuses a referenced delete, so surfacing the referencing prompts here
 * turns a rejected request into an informed decision — the user sees what to fix
 * rather than a conflict error.
 */
function DeleteListConfirm({
	list,
	isDeleting,
	onCancel,
	onConfirm,
}: {
	list: PromptListRecord
	isDeleting: boolean
	onCancel: () => void
	onConfirm: () => void
}) {
	const references = usePromptListReferences(list.id)
	const blocking = (references.data ?? []).length > 0

	return (
		<ConfirmDialog
			open
			title={`Delete “${list.name}”?`}
			confirmLabel="Delete"
			busyLabel="Deleting…"
			confirmVariant="danger"
			submitting={isDeleting}
			// Refused while it is still referenced, and while the check is in flight —
			// deleting on an unknown answer is the case this dialog exists to prevent.
			confirmDisabled={blocking || references.isLoading}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			{references.isLoading ? (
				<p className="text-ds-sm text-ds-text-muted">Checking references…</p>
			) : blocking ? (
				<>
					<p className="text-ds-sm text-ds-danger">
						This list is referenced by {references.data?.length} prompt(s). Remove those references first — deleting it
						would leave them unrenderable.
					</p>
					<ul className="mt-2 space-y-1">
						{(references.data ?? []).map((r) => (
							<li key={r.id} className="font-mono text-ds-2xs text-ds-text-subtle">
								{r.code}
							</li>
						))}
					</ul>
				</>
			) : (
				<p className="text-ds-sm text-ds-text-muted">
					Nothing references this list, so deleting it is safe. It is soft-deleted, and its code stays reserved — codes
					are never reused.
				</p>
			)}
		</ConfirmDialog>
	)
}
